import React, { useState } from 'react';
import { ShieldAlert, Plus, Trash2, ShieldCheck, AlertTriangle, ShieldInfo, Info } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ContentModeration = () => {
  const [blacklist, setBlacklist] = useState(["robux_hack", "exploit", "executor", "synapse", "cookie_logger", "condo_game", "bypass_chat"]);
  const [newWord, setNewWord] = useState("");

  const handleAddWord = (e) => {
    e.preventDefault();
    const word = newWord.trim().toLowerCase();
    if (!word) return;
    if (blacklist.includes(word)) {
      toast.error("Vector already filtered.");
      return;
    }
    setBlacklist([...blacklist, word]);
    setNewWord("");
    toast.success("Security vector updated.");
  };

  const handleRemoveWord = (word) => {
    setBlacklist(blacklist.filter(w => w !== word));
    toast.info("Vector filter removed.");
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      <section>
        <h1 className="text-2xl font-syne font-bold">Security & Integrity</h1>
        <p className="text-sonas-muted text-xs mt-1">Platform-wide threat mitigation and content filtering.</p>
      </section>

      <Tabs defaultValue="blacklist" className="w-full">
        <TabsList className="bg-sonas-surface2 border border-sonas-border p-1 h-12 rounded-2xl mb-8">
          <TabsTrigger value="queue" className="rounded-xl px-8 data-[state=active]:bg-sonas-accent data-[state=active]:text-sonas-bg font-bold">
            Flagged Streams
          </TabsTrigger>
          <TabsTrigger value="blacklist" className="rounded-xl px-8 data-[state=active]:bg-sonas-accent data-[state=active]:text-sonas-bg font-bold">
            Vector Filtering
          </TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="py-20 flex flex-col items-center justify-center text-center space-y-4">
          <div className="h-16 w-16 bg-sonas-accent/10 rounded-[24px] flex items-center justify-center border border-sonas-accent/20">
            <ShieldCheck className="h-8 w-8 text-sonas-accent" />
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-bold font-syne">All Clear</h3>
            <p className="text-xs text-sonas-muted max-w-xs mx-auto">
              No content violations detected in the last 24 hours. Platform integrity is nominal.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="blacklist" className="space-y-10">
          <div className="bg-sonas-surface border border-sonas-border p-8 rounded-3xl space-y-6">
            <div className="space-y-2">
              <h3 className="font-syne font-bold">Restrict Keywords</h3>
              <p className="text-xs text-sonas-muted">Incoming prompts containing these vectors will be automatically blocked by the firewall.</p>
            </div>

            <form onSubmit={handleAddWord} className="flex gap-3">
              <div className="relative flex-1">
                <ShieldAlert className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-sonas-dim" />
                <Input 
                  placeholder="Enter forbidden vector (e.g. bypass_chat)..." 
                  className="pl-12 h-12 bg-sonas-surface2 border-sonas-border rounded-2xl focus:ring-sonas-accent"
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                />
              </div>
              <Button type="submit" className="h-12 px-8 bg-sonas-text text-sonas-bg font-bold rounded-2xl gap-2 hover:opacity-90">
                <Plus size={18} /> Add Vector
              </Button>
            </form>

            <div className="flex flex-wrap gap-2.5 pt-4">
              {blacklist.map((word) => (
                <div key={word} className="flex items-center gap-2.5 pl-4 pr-2 py-2 bg-sonas-surface2 border border-sonas-border rounded-xl group hover:border-red-500/30 transition-all">
                  <span className="text-[11px] font-bold font-mono text-sonas-dim group-hover:text-sonas-text">{word}</span>
                  <button 
                    onClick={() => handleRemoveWord(word)}
                    className="h-6 w-6 rounded-lg flex items-center justify-center text-sonas-dim hover:bg-red-500 hover:text-white transition-all"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContentModeration;